import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../../filters/translate.pipe';
import ChatRestServices from '../../../services/chat-rest.service';
import ChatService from '../../../services/chat.service';

@Component({
	standalone: true,
	selector: 'toolkit-clients',
	templateUrl: './toolkit-clients.component.html',
	imports: [CommonModule, FormsModule, TranslatePipe]
})
export class ToolkitClientsComponent {
	private chatRestServices: ChatRestServices = inject(ChatRestServices);
	private chatService: ChatService = inject(ChatService);

	@Input() vm: any;

	clientSummary(c: any): any {
		const fn = this.vm?.getClientSummary;
		return typeof fn === 'function' ? fn(c) : null;
	}

	effectivePrompt(c: any): any | null {
		const fn = this.vm?.getEffectivePrompt;
		return typeof fn === 'function' ? fn(c) : (c?.assessmentPrompt || null);
	}

	promptState(c: any): string {
		return (this.effectivePrompt(c)?.state || '').toString().toLowerCase();
	}

	hasVisiblePrompt(c: any): boolean {
		return ['due', 'sent', 'overdue', 'completed'].includes(this.promptState(c));
	}

	promptActionLabel(c: any): string {
		const state = this.promptState(c);
		if (state === 'due' && (this.reassessmentSessionIndex(c) === 4 || this.reassessmentSessionIndex(c) === 8)) {
			return this.t('Toolkit.Prep.Action.Assign', 'Assign EEK-2');
		}
		if (state === 'due') return this.t('Toolkit.Prep.Action.Assign', 'Assign EEK-2');
		if (state === 'sent' || state === 'overdue') return this.t('Toolkit.Prep.Action.Remind', 'Remind client');
		if (state === 'completed') return this.t('Toolkit.Prep.Action.ViewResult', 'View result');
		return this.t('Toolkit.Provider.Client.Actions.Assign', 'Assign');
	}

	handlePromptAction(c: any) {
		const state = this.promptState(c);
		if (state === 'due' && (this.reassessmentSessionIndex(c) === 4 || this.reassessmentSessionIndex(c) === 8)) {
			this.vm?.startPromptAssign?.(c);
			return;
		}
		if (state === 'due') {
			this.vm?.startPromptAssign?.(c);
			return;
		}
		if (state === 'completed') {
			this.vm?.openPromptResult?.(c);
			return;
		}
		if (state === 'sent' || state === 'overdue') {
			this.openChat(c);
			return;
		}
		this.vm?.startAssignForClient?.(c);
	}

	statusPill(summary: any): { cls: string; key: string; count?: number; translate?: boolean } {
		if (summary?.highlightReassessment) return { cls: 'pill-eek2-due', key: 'Toolkit.Prep.Status.Due', translate: true };
		const key = (summary?.statusKey || 'ACTIVE').toString().toUpperCase();
		if (key === 'NEW') return { cls: 'pill-new', key: 'Toolkit.Status.NewClient', translate: true };
		if (key === 'DONE') return { cls: 'pill-done', key: 'Toolkit.Status.Done', translate: true };
		if (key === 'OVERDUE') {
			const n = Number(summary?.overdueCount || 0);
			const count = isFinite(n) && n > 0 ? n : undefined;
			return { cls: 'pill-overdue', key: 'Toolkit.Status.Overdue', count, translate: true };
		}
		return { cls: 'pill-active', key: 'Toolkit.Status.Active', translate: true };
	}

	isReassessmentDue(summary: any): boolean {
		return !!summary?.highlightReassessment;
	}

	reassessmentTitle(summary: any): string {
		const sessionIndex = summary?.promptSessionIndex || '';
		return this.t('Toolkit.Clients.Prompt.Alert.Reassessment.Title', 'EEK-2 re-assessment due before session %s')
			.replace('%s', `${sessionIndex || ''}`);
	}

	reassessmentBody(c: any): string {
		const prompt = this.effectivePrompt(c);
		if (!prompt) return '';

		const baselineDate = prompt?.baselineAt ? this.vm?.formatShortDate?.(prompt.baselineAt) || '' : '';
		const baselineScore = prompt?.baselineScore;

		if (baselineDate && baselineScore != null) {
			return this.t(
				'Toolkit.Clients.Prompt.Alert.Reassessment.Body.WithBaseline',
				'Baseline %s: %s · send now so the score is ready in session'
			)
				.replace('%s', baselineDate)
				.replace('%s', `${baselineScore}`);
		}

		if (baselineScore != null) {
			return this.t(
				'Toolkit.Clients.Prompt.Alert.Reassessment.Body.WithScore',
				'Baseline score %s · send now so the score is ready in session'
			).replace('%s', `${baselineScore}`);
		}

		return this.t(
			'Toolkit.Clients.Prompt.Alert.Reassessment.Body.Default',
			'Send now so the score is ready in session'
		);
	}

	statusCls(summary: any): string {
		const key = (summary?.statusKey || 'active').toString();
		if (key === 'new') return 'pill-new';
		if (key === 'done') return 'pill-done';
		if (key === 'overdue') return 'pill-overdue';
		return 'pill-active';
	}

	openChat(c: any) {
		const friend = c || null;
		const id = friend?.id;
		if (!id) return;
		this.chatRestServices.getChatByFriendId({ friendUserId: id }, (chat) => {
			this.chatService.openChatById(friend, chat?.id);
		});
	}

	progressPercent(done: number, total: number): number {
		if (!total || total <= 0) return 0;
		return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
	}

	progressFillClass(done: number, total: number): string {
		const pct = this.progressPercent(done, total);
		if (pct <= 0) return 'fill-gray';
		if (pct >= 100) return 'fill-green';
		return 'fill-orange';
	}

	planNodes(client: any): string[] {
		const plan = client?.planProgress || null;
		const total = Math.max(0, Number(plan?.total || 0));
		const derivedCompleted = this.vm?.getPlanCompletedCount;
		const completed = typeof derivedCompleted === 'function'
			? Math.max(0, Number(derivedCompleted(client) || 0))
			: Math.max(0, Number(plan?.completed || 0));
		const isDue = (client?.assessmentPrompt?.state || '').toString().toLowerCase() === 'due';
		const dueIndex = isDue && plan?.dueTimepoint ? this.planIndex(plan.dueTimepoint) : -1;
		const out: string[] = [];
		for (let i = 0; i < total; i++) {
			if (i < completed) out.push('done');
			else if (i === dueIndex) out.push('due');
			else out.push('todo');
		}
		return out;
	}

	private planIndex(timepoint: any): number {
		const key = (timepoint || '').toString().trim().toUpperCase();
		if (key === 'T1_BASELINE') return 0;
		if (key === 'T2') return 1;
		if (key === 'T3') return 2;
		return -1;
	}

	private reassessmentSessionIndex(c: any): number {
		const numeric = Number(c?.assessmentPrompt?.sessionIndex ?? c?.nextBooking?.sessionIndex ?? c?.nextSessionIndex ?? c?.sessionIndex ?? 0);
		return isFinite(numeric) ? numeric : 0;
	}

	private t(key: string, fallback: string): string {
		return this.vm?.angularFilterHelperService?.translate?.(key) || fallback;
	}
}
